import React, { useState, useRef, useMemo } from 'react';
import { ALL_POKEMON, teams as PRESET_TEAMS } from '../data/teams';
import { getSpriteUrl } from '../data/sprites';
import { ARCHETYPE_OPTIONS, emptyStrategy, normalizeStrategy } from '../data/teamStrategy';
import './StrategyEditor.css';

// Presets that ship with a structured advisorStrategy — surfaced as
// "Import from preset" options so a user can seed their saved team's
// strategy from a curated example.
const PRESETS_WITH_STRATEGY = PRESET_TEAMS.filter(t => t.advisorStrategy);

// Free-text Pokemon picker: matches any of the ~1000 mons in ALL_POKEMON.
// Used for the "when opponent brings X" side of triggers.
function PokemonPicker({ value, onSelect, placeholder, exclude = [] }) {
  const [query, setQuery] = useState(value || '');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef();

  const results = useMemo(() => {
    if (query.length < 1) return [];
    const q = query.toLowerCase();
    return ALL_POKEMON
      .filter(p => p.name.toLowerCase().includes(q) && !exclude.includes(p.name))
      .slice(0, 8);
  }, [query, exclude]);

  return (
    <div className="strat-picker">
      <input
        ref={inputRef}
        type="text"
        value={query}
        placeholder={placeholder || 'Pokemon...'}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
      />
      {focused && results.length > 0 && (
        <div className="strat-picker-results">
          {results.map(p => (
            <div
              key={p.name}
              className="strat-picker-result"
              onMouseDown={() => {
                onSelect(p.name);
                setQuery('');
                inputRef.current?.blur();
              }}
            >
              <img src={getSpriteUrl(p.name)} alt="" className="strat-picker-sprite" onError={e => { e.target.style.display = 'none'; }} />
              {p.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NameTagList({ names, onRemove }) {
  if (!names || names.length === 0) return null;
  return (
    <div className="strat-tags">
      {names.map(n => (
        <span key={n} className="strat-tag">
          <img src={getSpriteUrl(n)} alt="" className="strat-tag-sprite" onError={e => { e.target.style.display = 'none'; }} />
          {n}
          <button className="strat-tag-x" onClick={() => onRemove(n)} title="Remove">×</button>
        </span>
      ))}
    </div>
  );
}

export default function StrategyEditor({ teamPokemon, strategy, onChange, onSaveImmediate }) {
  const s = normalizeStrategy(strategy || emptyStrategy());
  const [section, setSection] = useState('priors');

  const update = (patch) => onChange({ ...s, ...patch });

  // ---- Priors ----
  const setPrior = (name, value) => {
    const next = { ...s.bringPriors, [name]: value };
    update({ bringPriors: next });
  };

  // ---- Triggers ----
  const addTrigger = () => {
    const id = String(Math.random()).slice(2);
    update({
      triggers: [...s.triggers, {
        id,
        when: { sees: [], andAlso: [], megaOnly: false, archetype: '' },
        bring: [],
        reason: '',
      }],
    });
  };
  const updateTrigger = (id, patch) => {
    update({
      triggers: s.triggers.map(t => t.id === id ? { ...t, ...patch } : t),
    });
  };
  const updateTriggerWhen = (id, whenPatch) => {
    update({
      triggers: s.triggers.map(t => t.id === id ? { ...t, when: { ...t.when, ...whenPatch } } : t),
    });
  };
  const removeTrigger = (id) => update({ triggers: s.triggers.filter(t => t.id !== id) });

  // ---- Lead rules ----
  const updateLeadRules = (patch) => update({ leadRules: { ...s.leadRules, ...patch } });
  const addAvoid = () => {
    updateLeadRules({
      avoid: [...s.leadRules.avoid, { lead: teamPokemon[0]?.name || '', into: [], reason: '' }],
    });
  };
  const updateAvoid = (idx, patch) => {
    const next = [...s.leadRules.avoid];
    next[idx] = { ...next[idx], ...patch };
    updateLeadRules({ avoid: next });
  };
  const removeAvoid = (idx) => {
    updateLeadRules({ avoid: s.leadRules.avoid.filter((_, i) => i !== idx) });
  };

  // ---- Setup scripts ----
  const addScript = () => update({
    setupScripts: [...s.setupScripts, { mon: teamPokemon[0]?.name || '', vs: [], script: '' }],
  });
  const updateScript = (idx, patch) => {
    const next = [...s.setupScripts];
    next[idx] = { ...next[idx], ...patch };
    update({ setupScripts: next });
  };
  const removeScript = (idx) => update({
    setupScripts: s.setupScripts.filter((_, i) => i !== idx),
  });

  // ---- Key 1v1s ----
  const add1v1 = () => update({
    keyOneVOnes: [...s.keyOneVOnes, { mon: teamPokemon[0]?.name || '', vs: '', note: '' }],
  });
  const update1v1 = (idx, patch) => {
    const next = [...s.keyOneVOnes];
    next[idx] = { ...next[idx], ...patch };
    update({ keyOneVOnes: next });
  };
  const remove1v1 = (idx) => update({
    keyOneVOnes: s.keyOneVOnes.filter((_, i) => i !== idx),
  });

  const importFromPreset = async (presetId) => {
    const preset = PRESETS_WITH_STRATEGY.find(p => p.id === presetId);
    if (!preset) return;
    const strat = normalizeStrategy(preset.advisorStrategy);
    const hasExisting =
      Object.keys(s.bringPriors).length > 0 ||
      s.triggers.length > 0 ||
      s.leadRules.avoid.length > 0 ||
      s.leadRules.defaultLead ||
      s.setupScripts.length > 0 ||
      s.keyOneVOnes.length > 0;
    if (hasExisting && !window.confirm(`Replace your current strategy with the "${preset.name}" preset? This overwrites everything.`)) {
      return;
    }
    onChange(strat);
    // Auto-persist so the import is saved immediately — otherwise the editor
    // state changes but the team in Firestore stays empty until the user
    // remembers to click Save Team. The advisor only reads the persisted
    // strategy, so without this the import has no visible effect.
    if (onSaveImmediate) {
      try {
        await onSaveImmediate(strat);
        window.alert(`Imported "${preset.name}" strategy and saved to your team.`);
      } catch (err) {
        console.error('Failed to auto-save imported strategy:', err);
        window.alert(`Strategy imported into the editor but NOT saved: ${err.message || err}\n\nClick "Save Team" to persist it.`);
      }
    } else {
      window.alert(`Imported "${preset.name}" strategy. Click "Save Team" to persist.`);
    }
  };

  return (
    <div className="strat-editor">
      <div className="strat-editor-head">
        <h4>Strategy</h4>
        <p className="strat-editor-blurb">
          Tell the advisor how you actually play this team — bring rates, "if you see X bring Y" rules, and lead pivots.
          Without these, it can only reason from raw damage calcs.
        </p>
        {PRESETS_WITH_STRATEGY.length > 0 && (
          <div className="strat-import">
            <label>Import from preset:</label>
            {PRESETS_WITH_STRATEGY.map(p => (
              <button
                key={p.id}
                type="button"
                className="strat-preset-btn"
                onClick={() => importFromPreset(p.id)}
                title={`Apply ${p.name} strategy and save it to this team`}
              >
                Apply "{p.name}"
              </button>
            ))}
            {onSaveImmediate && (
              <button
                type="button"
                className="strat-save-btn"
                onClick={async () => {
                  try {
                    await onSaveImmediate(s);
                    window.alert('Strategy saved to your team.');
                  } catch (err) {
                    window.alert(`Save failed: ${err.message || err}`);
                  }
                }}
                title="Force-save the current strategy to Firestore right now"
              >
                Save Strategy Now
              </button>
            )}
          </div>
        )}
      </div>

      <div className="strat-tabs">
        <button className={`strat-tab ${section === 'priors' ? 'active' : ''}`} onClick={() => setSection('priors')}>
          Bring rates
        </button>
        <button className={`strat-tab ${section === 'triggers' ? 'active' : ''}`} onClick={() => setSection('triggers')}>
          Triggers ({s.triggers.length})
        </button>
        <button className={`strat-tab ${section === 'leads' ? 'active' : ''}`} onClick={() => setSection('leads')}>
          Lead rules ({s.leadRules.avoid.length})
        </button>
        <button className={`strat-tab ${section === 'scripts' ? 'active' : ''}`} onClick={() => setSection('scripts')}>
          Setup scripts ({s.setupScripts.length})
        </button>
        <button className={`strat-tab ${section === '1v1s' ? 'active' : ''}`} onClick={() => setSection('1v1s')}>
          Key 1v1s ({s.keyOneVOnes.length})
        </button>
      </div>

      {section === 'priors' && (
        <div className="strat-section">
          <p className="strat-hint">
            How often do you bring each Pokemon? 0% = preview-only scarecrow. 100% = bring every game.
            The advisor uses this to break ties when matchups look similar.
          </p>
          <div className="strat-priors-list">
            {teamPokemon.length === 0 && <div className="strat-empty">Add Pokemon to your team first.</div>}
            {teamPokemon.map(p => {
              const v = typeof s.bringPriors[p.name] === 'number' ? s.bringPriors[p.name] : 0.5;
              return (
                <div key={p.name} className="strat-prior-row">
                  <img src={getSpriteUrl(p.name)} alt="" className="strat-prior-sprite" onError={e => { e.target.style.display = 'none'; }} />
                  <span className="strat-prior-name">{p.name}</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={Math.round(v * 100)}
                    onChange={e => setPrior(p.name, Number(e.target.value) / 100)}
                  />
                  <span className="strat-prior-pct">{Math.round(v * 100)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {section === 'triggers' && (
        <div className="strat-section">
          <p className="strat-hint">
            "When the opponent brings <em>X</em>, bring <em>Y</em>." e.g. "if you see Toxapex + Corviknight → bring Dragonite + Basculegion + Floette".
          </p>
          {s.triggers.length === 0 && <div className="strat-empty">No triggers yet.</div>}
          {s.triggers.map(t => (
            <div key={t.id} className="strat-trigger">
              <div className="strat-trigger-row">
                <label>When opponent brings:</label>
                <NameTagList names={t.when.sees} onRemove={n => updateTriggerWhen(t.id, { sees: t.when.sees.filter(x => x !== n) })} />
                <PokemonPicker
                  placeholder="Add..."
                  exclude={t.when.sees}
                  onSelect={n => updateTriggerWhen(t.id, { sees: [...t.when.sees, n] })}
                />
                <label className="strat-checkbox">
                  <input
                    type="checkbox"
                    checked={t.when.megaOnly}
                    onChange={e => updateTriggerWhen(t.id, { megaOnly: e.target.checked })}
                  />
                  Mega only
                </label>
              </div>

              <div className="strat-trigger-row">
                <label>And also (all required):</label>
                <NameTagList names={t.when.andAlso} onRemove={n => updateTriggerWhen(t.id, { andAlso: t.when.andAlso.filter(x => x !== n) })} />
                <PokemonPicker
                  placeholder="Optional..."
                  exclude={t.when.andAlso}
                  onSelect={n => updateTriggerWhen(t.id, { andAlso: [...t.when.andAlso, n] })}
                />
              </div>

              <div className="strat-trigger-row">
                <label>Archetype match:</label>
                <select
                  value={t.when.archetype}
                  onChange={e => updateTriggerWhen(t.id, { archetype: e.target.value })}
                >
                  <option value="">(any)</option>
                  {ARCHETYPE_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>

              <div className="strat-trigger-row">
                <label>→ Bring:</label>
                <div className="strat-checkboxes">
                  {teamPokemon.map(p => (
                    <label key={p.name} className="strat-checkbox">
                      <input
                        type="checkbox"
                        checked={t.bring.includes(p.name)}
                        onChange={e => {
                          const next = e.target.checked
                            ? [...t.bring, p.name]
                            : t.bring.filter(n => n !== p.name);
                          updateTrigger(t.id, { bring: next });
                        }}
                      />
                      <img src={getSpriteUrl(p.name)} alt="" className="strat-tag-sprite" onError={e => { e.target.style.display = 'none'; }} />
                      {p.name}
                    </label>
                  ))}
                </div>
              </div>

              <div className="strat-trigger-row">
                <label>Why:</label>
                <input
                  type="text"
                  className="strat-text"
                  value={t.reason}
                  placeholder="Optional reason — shown in battle plan"
                  onChange={e => updateTrigger(t.id, { reason: e.target.value })}
                />
              </div>

              <button className="strat-remove-btn" onClick={() => removeTrigger(t.id)}>Delete trigger</button>
            </div>
          ))}
          <button className="strat-add-btn" onClick={addTrigger}>+ Add trigger</button>
        </div>
      )}

      {section === 'leads' && (
        <div className="strat-section">
          <p className="strat-hint">
            Default lead and "don't lead X into Y" rules. e.g. "Floette is the default lead" + "Don't lead Floette into Gengar / Starmie".
          </p>
          <div className="strat-lead-default">
            <label>Default lead:</label>
            <select
              value={s.leadRules.defaultLead || ''}
              onChange={e => updateLeadRules({ defaultLead: e.target.value })}
            >
              <option value="">(no default)</option>
              {teamPokemon.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
            </select>
          </div>

          {s.leadRules.avoid.map((rule, idx) => (
            <div key={idx} className="strat-trigger">
              <div className="strat-trigger-row">
                <label>Don't lead:</label>
                <select
                  value={rule.lead}
                  onChange={e => updateAvoid(idx, { lead: e.target.value })}
                >
                  <option value="">(pick mon)</option>
                  {teamPokemon.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                </select>
                <label>Into:</label>
                <NameTagList names={rule.into} onRemove={n => updateAvoid(idx, { into: rule.into.filter(x => x !== n) })} />
                <PokemonPicker
                  placeholder="Add opp..."
                  exclude={rule.into}
                  onSelect={n => updateAvoid(idx, { into: [...rule.into, n] })}
                />
              </div>
              <div className="strat-trigger-row">
                <label>Why:</label>
                <input
                  type="text"
                  className="strat-text"
                  value={rule.reason}
                  placeholder="Optional"
                  onChange={e => updateAvoid(idx, { reason: e.target.value })}
                />
              </div>
              <button className="strat-remove-btn" onClick={() => removeAvoid(idx)}>Delete rule</button>
            </div>
          ))}
          <button className="strat-add-btn" onClick={addAvoid}>+ Add avoid-lead rule</button>
        </div>
      )}

      {section === 'scripts' && (
        <div className="strat-section">
          <p className="strat-hint">
            Step-by-step play scripts shown when relevant. e.g. "Volcarona vs Kangaskhan: QD on Double-Edge → Sitrus → Morning Sun → Fiery Dance burn."
          </p>
          {s.setupScripts.map((script, idx) => (
            <div key={idx} className="strat-trigger">
              <div className="strat-trigger-row">
                <label>My mon:</label>
                <select value={script.mon} onChange={e => updateScript(idx, { mon: e.target.value })}>
                  <option value="">(pick mon)</option>
                  {teamPokemon.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                </select>
                <label>vs:</label>
                <NameTagList names={script.vs} onRemove={n => updateScript(idx, { vs: script.vs.filter(x => x !== n) })} />
                <PokemonPicker
                  placeholder="Add opp..."
                  exclude={script.vs}
                  onSelect={n => updateScript(idx, { vs: [...script.vs, n] })}
                />
              </div>
              <div className="strat-trigger-row">
                <label>Script:</label>
                <textarea
                  className="strat-textarea"
                  rows={2}
                  value={script.script}
                  placeholder="QD on Double-Edge → Sitrus → Morning Sun → Fiery Dance burn"
                  onChange={e => updateScript(idx, { script: e.target.value })}
                />
              </div>
              <button className="strat-remove-btn" onClick={() => removeScript(idx)}>Delete script</button>
            </div>
          ))}
          <button className="strat-add-btn" onClick={addScript}>+ Add setup script</button>
        </div>
      )}

      {section === '1v1s' && (
        <div className="strat-section">
          <p className="strat-hint">
            Quick 1v1 facts you want surfaced on the bring card. e.g. "Floette vs Kangaskhan after Protect: WIN with Chilling Water".
          </p>
          {s.keyOneVOnes.map((row, idx) => (
            <div key={idx} className="strat-trigger">
              <div className="strat-trigger-row">
                <label>My mon:</label>
                <select value={row.mon} onChange={e => update1v1(idx, { mon: e.target.value })}>
                  <option value="">(pick mon)</option>
                  {teamPokemon.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                </select>
                <label>vs:</label>
                <PokemonPicker
                  placeholder="Opp..."
                  onSelect={n => update1v1(idx, { vs: n })}
                />
                {row.vs && (
                  <span className="strat-tag">
                    <img src={getSpriteUrl(row.vs)} alt="" className="strat-tag-sprite" onError={e => { e.target.style.display = 'none'; }} />
                    {row.vs}
                    <button className="strat-tag-x" onClick={() => update1v1(idx, { vs: '' })}>×</button>
                  </span>
                )}
              </div>
              <div className="strat-trigger-row">
                <label>Note:</label>
                <input
                  type="text"
                  className="strat-text"
                  value={row.note}
                  placeholder="WIN with Chilling Water after Protect"
                  onChange={e => update1v1(idx, { note: e.target.value })}
                />
              </div>
              <button className="strat-remove-btn" onClick={() => remove1v1(idx)}>Delete</button>
            </div>
          ))}
          <button className="strat-add-btn" onClick={add1v1}>+ Add key 1v1</button>
        </div>
      )}
    </div>
  );
}
